import React, { useState, useEffect } from 'react';
import { Conversation } from '../types';
import { Phone as PhoneIcon, Users } from 'lucide-react';
const chatProfile = require('../assets/chat-profile.png');

interface Phone {
  phone: string;
  name?: string;
}

interface ConversationsListProps {
  conversations: Conversation[];
  selectedConversation: string | null;
  onSelectConversation: (conversationId: string) => void;
  loading: boolean;
  userPhone: string;
  onLoadConversations: (silent?: boolean, filterPhone?: string) => void;
  onPhoneSelected?: (phone: string) => void;
  onSearchPhones?: (searchTerm: string) => Promise<Phone[]>;
  availablePhones?: string[];
  phoneData?: Phone[];
  urlPhone?: string;
}

export const ConversationsList: React.FC<ConversationsListProps> = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  loading,
  userPhone,
  onLoadConversations,
  onPhoneSelected,
  onSearchPhones,
  availablePhones = [],
  phoneData = [],
  urlPhone
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPhone, setSelectedPhone] = useState<string>('');
  const [selectedPhoneName, setSelectedPhoneName] = useState<string>('');
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [phoneSearchTerm, setPhoneSearchTerm] = useState('');
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const [filteredPhones, setFilteredPhones] = useState<Phone[]>([]);

  useEffect(() => {
    const searchPhones = async () => {
      if (phoneSearchTerm.length < 2) {
        setFilteredPhones([]);
        return;
      }

      if (onSearchPhones) {
        setLoadingPhones(true);
        try {
          const results = await onSearchPhones(phoneSearchTerm);
          setFilteredPhones(results);
        } catch (error) {
          console.error('Error searching phones:', error);
          setFilteredPhones([]);
        } finally {
          setLoadingPhones(false);
        }
      }
    };

    const debounceTimer = setTimeout(searchPhones, 800);
    return () => clearTimeout(debounceTimer);
  }, [phoneSearchTerm, onSearchPhones]);


  useEffect(() => {
    if (urlPhone) {
      setSelectedPhone(urlPhone);
      setSelectedPhoneName(urlPhone);
      onLoadConversations(false, urlPhone);
    } else if (selectedPhone && selectedPhone !== '') {
      onLoadConversations(false, selectedPhone);
    }
  }, [selectedPhone, onLoadConversations, urlPhone]);

  const handlePhoneChange = (phone: string, name?: string) => {
    setSelectedPhone(phone);
    
    if (phone) {
      if (name) {
        setSelectedPhoneName(name);
      } else {
        const phoneInfo = filteredPhones.find(p => p.phone === phone);
        if (phoneInfo && phoneInfo.name) {
          setSelectedPhoneName(phoneInfo.name);
        } else {
          setSelectedPhoneName(phone);
        }
      }
      
      if (onPhoneSelected) {
        onPhoneSelected(phone);
      }
    } else {
      setSelectedPhoneName('');
    }
  };


  const getConversationPhone = (conversation: Conversation): string => {
    if (conversation.participant_phone) {
      return conversation.participant_phone;
    }
    
    if (conversation.participant_name && /^[0-9+]+$/.test(conversation.participant_name)) {
      return conversation.participant_name;
    }
    
    const chatId = conversation.chat_id;
    
    if (chatId.includes('_') && userPhone) {
      const phones = chatId.split('_');
      const cleanUserPhone = userPhone.replace(/\D/g, '');
      
      const otherPhone = phones.find(phone => {
        const cleanPhone = phone.replace(/\D/g, '');
        const isValidPhone = /^[0-9+]+$/.test(phone);
        const isLongEnough = cleanPhone.length >= 10;
        return isValidPhone &&
               isLongEnough &&
               cleanPhone !== cleanUserPhone && 
               !cleanPhone.includes(cleanUserPhone) && 
               !cleanUserPhone.includes(cleanPhone);
      });
      
      if (otherPhone) {
        return otherPhone;
      }
    }
    
    if (conversation.is_group) {
      return 'Grupo';
    }
    
    return '';
  };

  const getConversationName = (conversation: Conversation): string => {
    const isUUID = (str: string): boolean => {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
    };
    
    if (conversation.participant_name && conversation.participant_name.trim()) {
      if (!isUUID(conversation.participant_name)) {
        return conversation.participant_name;
      }
    }
    
    if (conversation.is_group) {
      return conversation.group_name || 'Sem nome';
    }
    
    const chatId = conversation.chat_id;
    if (chatId.includes('_') && userPhone) {
      const phones = chatId.split('_');
      const cleanUserPhone = userPhone.replace(/\D/g, '');
      
      const otherPhone = phones.find(phone => {
        const cleanPhone = phone.replace(/\D/g, '');
        const isValidPhone = /^[0-9+]+$/.test(phone);
        return isValidPhone &&
               cleanPhone !== cleanUserPhone && 
               !cleanPhone.includes(cleanUserPhone) && 
               !cleanUserPhone.includes(cleanPhone);
      });
      
      if (otherPhone) {
        const phoneInfo = phoneData.find(p => p.phone === otherPhone);
        if (phoneInfo && phoneInfo.name) {
          return phoneInfo.name;
        }
        return otherPhone;
      }
    }
    
    return 'Contato sem nome';
  };


  const filteredConversations = conversations.filter(conversation => {
    const name = getConversationName(conversation).toLowerCase();
    const chatId = conversation.chat_id.toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return name.includes(search) || chatId.includes(search);
  });

  if (loading && conversations.length === 0) {
    return (
      <div className="loading-conversations">
        <div className="spinner"></div>
        <div className="loading-text">Carregando conversas...</div>
      </div>
    );
  }

  return (
    <div className="conversations-list">
      {!urlPhone && (
        <div className="phone-selector-bar">
          <div className="phone-autocomplete-wrapper">
            <input
              type="text"
              className="phone-select phone-autocomplete-input"
              placeholder={selectedPhoneName || "Digite para buscar um número ou nome..."}
              value={phoneSearchTerm}
              onChange={(e) => setPhoneSearchTerm(e.target.value)}
              onFocus={() => setShowPhoneDropdown(true)}
              onBlur={() => setTimeout(() => setShowPhoneDropdown(false), 200)}
              disabled={loadingPhones}
            />
            {showPhoneDropdown && phoneSearchTerm.length >= 2 && (
              <div className="phone-autocomplete-dropdown">
                {loadingPhones && (
                  <div className="phone-autocomplete-loading">
                    Buscando...
                  </div>
                )}
                {!loadingPhones && filteredPhones.length > 0 && (
                  <>
                    {filteredPhones.map(phone => (
                      <div
                        key={phone.phone}
                        className="phone-autocomplete-option"
                        onClick={() => {
                          handlePhoneChange(phone.phone, phone.name || undefined);
                          setPhoneSearchTerm('');
                          setShowPhoneDropdown(false);
                        }}
                      >
                        {phone.name ? (
                          <div>
                            <div className="phone-name">{phone.name}</div>
                            <div className="phone-number-small">{phone.phone}</div>
                          </div>
                        ) : (
                          phone.phone
                        )}
                      </div>
                    ))}
                  </>
                )}
                {!loadingPhones && phoneSearchTerm && filteredPhones.length === 0 && (
                  <div className="phone-autocomplete-no-results">
                    Nenhum resultado encontrado
                  </div>
                )}
              </div>
            )}
            {showPhoneDropdown && phoneSearchTerm.length > 0 && phoneSearchTerm.length < 2 && (
              <div className="phone-autocomplete-dropdown">
                <div className="phone-autocomplete-hint">
                  Digite pelo menos 2 caracteres para buscar
                </div>
              </div>
            )}
          </div>
          {selectedPhoneName && (
            <div className="selected-phone-display">
              <div className="selected-name">{selectedPhoneName}</div>
              {selectedPhoneName !== selectedPhone && (
                <div className="selected-number">{selectedPhone}</div>
              )}
            </div>
          )}
          {loadingPhones && <span className="loading-phones">Carregando...</span>}
        </div>
      )}
      
      {urlPhone && (
        <div className="phone-info-bar">
          <div className="phone-info">
            <div>Mostrando conversas para o telefone <strong>{urlPhone}</strong></div>
            {(() => {
              const phoneInfo = phoneData.find(p => p.phone === urlPhone);
              if (phoneInfo?.name) {
                return <div><strong>{phoneInfo.name}</strong></div>;
              }
              return null;
            })()}
          </div>
        </div>
      )}

      <div className="search-box">
        <input
          type="text"
          placeholder="Buscar conversas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>


      {!selectedPhone && (
        <div className="no-phone-selected">
          <p>Selecione um número para ver as conversas</p>
        </div>
      )}

      {selectedPhone && filteredConversations.length === 0 && !loading && (
        <div className="loading">
          Nenhuma conversa encontrada para este número
        </div>
      )}

      {filteredConversations.map((conversation, index) => {
        const conversationId = conversation.chat_id;
        const conversationName = getConversationName(conversation);
        
        const avatarLetter = conversationName.charAt(0).toUpperCase();
        let lastMessageDate = conversation.last_message_time 
          ? new Date(conversation.last_message_time)
          : null;
        
        if (lastMessageDate) {
          lastMessageDate.setHours(lastMessageDate.getHours());
        }
        
        const formatTime = (date: Date) => {
          const now = new Date();
          const isToday = date.toDateString() === now.toDateString();
          const isYesterday = new Date(now.getTime() - 86400000).toDateString() === date.toDateString();
          
          if (isToday) {
            return date.toLocaleTimeString('pt-BR', { 
              hour: '2-digit', 
              minute: '2-digit',
              timeZone: 'America/Sao_Paulo'
            });
          } else if (isYesterday) {
            return 'Ontem';
          } else {
            return date.toLocaleDateString('pt-BR', { 
              day: '2-digit', 
              month: '2-digit',
              timeZone: 'America/Sao_Paulo'
            });
          }
        };

        return (
          <div
            key={`${conversationId}-${index}`}
            className={`conversation-item ${selectedConversation === conversationId ? 'active' : ''}`}
            onClick={() => onSelectConversation(conversationId)}
          >
            <div className="conversation-avatar">
              <img 
                src={conversation.profile_picture_url || chatProfile} 
                alt={conversationName}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = avatarLetter;
                    parent.classList.add('fallback-avatar');
                  }
                }}
              />
            </div>
            
            <div className="conversation-info">
              <div className="conversation-header">
                <div className="conversation-name">
                  {conversationName}
                </div>
                {lastMessageDate && (
                  <div className="conversation-time">
                    {formatTime(lastMessageDate)}
                  </div>
                )}
              </div>
              
              <div className="conversation-preview">
                {!conversation.is_group ? (
                  <span className="phone-number">
                    <PhoneIcon size={12} color="black" fill="black" /> {getConversationPhone(conversation)}
                  </span>
                ) : (
                  <span className="group-info">
                    <Users size={14} /> Grupo
                  </span>
                )}
              </div>
              
            </div>
          </div>
        );
      })}
    </div>
  );
};
